'use client';

import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';

import axiosInstance from '@/lib/axios';
import { reportCreatePostFailure } from '@/lib/capture-create-post-error';
import { uploadFilesToVertex } from '@/lib/vertex-upload';
import { FormSubmitOverlay } from '@/components/forms/form-submit-overlay';
import { useCommunityMembers } from '@/hooks/use-community-members';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormTextareaField } from '@/components/forms/form-textarea-field';
import { FormComboboxField } from '@/components/forms/form-combobox-field';
import { FormRadioGroupField } from '@/components/forms/form-radio-group-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { useCommunityContext } from '@/providers/community-context';

const thankYouSchema = z
  .object({
    recipientId: z.string().min(1, 'Please select a member'),
    amount: z.string().min(1, 'Amount is required'),
    frequencySelection: z.enum(['One Time', 'Monthly', '6 Monthly', 'Yearly', 'Other']),
    otherFrequencyNumber: z.string().optional(),
    otherFrequencyUnit: z.enum(['Day', 'Week', 'Month', 'Year']).optional(),
    businessType: z.enum(['INTERNAL', 'EXTERNAL']),
    externalName: z.string().optional(),
    externalPhone: z.string().optional(),
    message: z
      .string()
      .min(10, 'Please write a heartfelt message')
      .max(500, 'Message cannot exceed 500 characters'),
  })
  .superRefine((data, ctx) => {
    if (data.frequencySelection === 'Other') {
      if (!data.otherFrequencyNumber) {
        ctx.addIssue({
          code: 'custom',
          message: 'Number is required',
          path: ['otherFrequencyNumber'],
        });
      }
      if (!data.otherFrequencyUnit) {
        ctx.addIssue({
          code: 'custom',
          message: 'Unit is required',
          path: ['otherFrequencyUnit'],
        });
      }
    }

    if (data.recipientId === 'Ex members' || data.recipientId === 'non member') {
      if (!data.externalName) {
        ctx.addIssue({
          code: 'custom',
          message: 'Name is required',
          path: ['externalName'],
        });
      }
      if (!data.externalPhone) {
        ctx.addIssue({
          code: 'custom',
          message: 'Phone number is required',
          path: ['externalPhone'],
        });
      }
    }
  });

const FREQUENCY_OPTIONS = [
  { value: 'One Time', label: 'One Time' },
  { value: 'Monthly', label: 'Monthly' },
  { value: '6 Monthly', label: '6 Monthly' },
  { value: 'Yearly', label: 'Yearly' },
  { value: 'Other', label: 'Other' },
];

const FREQUENCY_UNIT_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
  { value: 'Year', label: 'Year' },
];

interface ThankYouFormProps {
  onCancel: () => void;
  onSubmit: (postId: string) => void;
}

export function ThankYouForm({ onCancel, onSubmit: parentSubmit }: ThankYouFormProps) {
  const { communityId } = useParams<{ communityId: string }>();
  const { members, currentUserId } = useCommunityMembers(communityId!);

  const communityName = useCommunityContext().community.name;
  const specialOptions = [
    { label: `${communityName} Community`, value: communityName, group: 'Other' },
    { label: 'Ex members', value: 'Ex members', group: 'Other' },
    { label: 'Non member', value: 'non member', group: 'Other' },
  ];

  const memberOptions = members
    .filter((member) => member.id !== currentUserId)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((member) => ({
      label: member.businessName ? `${member.name} - ${member.businessName}` : member.name,
      value: member.id,
      group: 'Members',
    }));

  const allOptions = [...specialOptions, ...memberOptions];

  const form = useForm<z.infer<typeof thankYouSchema>>({
    resolver: zodResolver(thankYouSchema),
    defaultValues: {
      recipientId: '',
      amount: '',
      frequencySelection: 'One Time',
      otherFrequencyNumber: '',
      otherFrequencyUnit: 'Month',
      businessType: 'INTERNAL',
      externalName: '',
      externalPhone: '',
      message: '',
    },
  });

  const [searchParams] = useSearchParams();
  const prefillMemberId = searchParams.get('memberId');

  useEffect(() => {
    if (prefillMemberId) {
      form.setValue('recipientId', prefillMemberId);
    }
  }, [prefillMemberId, form]);

  const [docs] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<'idle' | 'uploading' | 'saving'>('idle');

  const frequencySelection = useWatch({ control: form.control, name: 'frequencySelection' });
  const recipientId = useWatch({ control: form.control, name: 'recipientId' });

  const onSubmit = async (values: z.infer<typeof thankYouSchema>) => {
    setIsSubmitting(true);
    setSubmitPhase(docs.length > 0 ? 'uploading' : 'saving');

    const isSpecial = [communityName, 'Ex members', 'non member'].includes(values.recipientId);

    try {
      const documentUrls = docs.length > 0 ? await uploadFilesToVertex(docs, 'thank-you') : [];

      const formData = new FormData();
      formData.append('recipientId', values.recipientId);
      formData.append('amount', values.amount.replace(/,/g, ''));

      if (values.frequencySelection === 'One Time') {
        formData.append('frequency', 'ONE_TIME');
      } else {
        formData.append('frequency', 'RECURRING');
        if (values.frequencySelection === 'Other') {
          formData.append(
            'recurringDuration',
            `${values.otherFrequencyNumber} ${values.otherFrequencyUnit}`
          );
        } else {
          formData.append('recurringDuration', values.frequencySelection);
        }
      }

      formData.append('businessType', isSpecial ? 'EXTERNAL' : 'INTERNAL');

      if (values.externalName) formData.append('externalName', values.externalName);
      if (values.externalPhone) formData.append('externalPhone', values.externalPhone);
      formData.append('message', values.message);
      formData.append('attachments', JSON.stringify(documentUrls));

      setSubmitPhase('saving');
      const { data } = await axiosInstance.post(
        `/api/communities/${communityId}/posts/thank-you`,
        formData
      );
      toast.success('Thank You Note Sent!');
      parentSubmit(data.postId);
    } catch (error) {
      toast.error(
        reportCreatePostFailure({
          kind: 'thank-you',
          error,
          userId: currentUserId,
          communityId,
          request: {
            recipientId: values.recipientId,
            amountLength: values.amount.length,
            frequencySelection: values.frequencySelection,
            businessType: isSpecial ? 'EXTERNAL' : 'INTERNAL',
            hasExternalName: Boolean(values.externalName),
            hasExternalPhone: Boolean(values.externalPhone),
            messageLength: values.message.length,
            documentCount: docs.length,
          },
          fallbackMessage: 'Failed to send thank you note. Please try again.',
        })
      );
    } finally {
      setIsSubmitting(false);
      setSubmitPhase('idle');
    }
  };

  return (
    <>
      <FormSubmitOverlay
        open={isSubmitting}
        title={submitPhase === 'uploading' ? 'Uploading files...' : 'Sending thank you note...'}
        description={
          submitPhase === 'uploading'
            ? 'Sending documents through secure media upload. Please keep this screen open.'
            : 'Saving your thank you note.'
        }
      />
      <Card className="mx-auto w-full max-w-5xl animate-in slide-in-from-bottom-5 border-0 bg-background/50 shadow-none backdrop-blur-md duration-300 md:border md:border-border md:shadow-2xl">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight text-foreground">
            Send a Thank You Note
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Acknowledge business closed with fellow members.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormComboboxField
                control={form.control}
                name="recipientId"
                label="Who are you thanking?"
                placeholder="Select Member"
                searchPlaceholder="Search member..."
                required
                options={allOptions}
              />

              {['Ex members', 'non member'].includes(recipientId) && (
                <div className="ml-1 grid grid-cols-1 gap-4 rounded-r-md border-l-2 border-primary/20 bg-muted/10 py-2 pl-4 pr-2 md:grid-cols-2">
                  <FormInputField
                    control={form.control}
                    name="externalName"
                    label="Person Name"
                    placeholder="John Doe"
                    required
                  />
                  <FormInputField
                    control={form.control}
                    name="externalPhone"
                    label="Contact Number"
                    placeholder="+1 234..."
                    required
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Amount of Business Transaction <span className="text-primary">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="0.00"
                        icon="₹"
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/[^0-9]/g, '');
                          if (!rawValue) {
                            field.onChange('');
                            return;
                          }
                          const formattedValue = parseInt(rawValue, 10).toLocaleString('en-IN');
                          field.onChange(formattedValue);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormRadioGroupField
                control={form.control}
                name="frequencySelection"
                label="Business Frequency"
                required
                layout="row"
                options={FREQUENCY_OPTIONS}
              />

              {frequencySelection === 'Other' && (
                <div className="border-l-2 border-primary/20 pl-4 ml-1 flex gap-4">
                  <FormInputField
                    control={form.control}
                    name="otherFrequencyNumber"
                    label="Number"
                    placeholder="e.g. 1"
                    type="number"
                    required
                  />
                  <FormSelectField
                    control={form.control}
                    name="otherFrequencyUnit"
                    label="Unit"
                    placeholder="Select unit"
                    options={FREQUENCY_UNIT_OPTIONS}
                    required
                  />
                </div>
              )}

              <FormTextareaField
                control={form.control}
                name="message"
                label="What message would you like to give?"
                placeholder="Write your note here... Use @ for a member or business, # for a category"
                maxLength={500}
                required
                mentions
              />

              <div className="flex flex-col-reverse justify-end gap-3 border-t border-border pt-6 md:flex-row">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!form.formState.isValid || isSubmitting}
                  className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
                >
                  {isSubmitting ? 'Sending...' : 'Send Thank You'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </>
  );
}
